// src/Services/statsCleanupJob.js
// This should be run periodically (daily) either as a cron job or Firebase scheduled function

import { 
  collection, 
  query, 
  where, 
  getDocs, 
  writeBatch,
  doc,
  setDoc,
  limit,
  orderBy
} from 'firebase/firestore';
import { db } from '../firebase';

export const runStatsCleanup = async () => {
  console.log('Starting statistics cleanup job...');
  
  try {
    // 1. Archive old daily stats (older than 90 days)
    await archiveOldDailyStats();
    
    // 2. Clean up old game events (older than 30 days)
    await cleanupOldGameEvents();
    
    // 3. Generate monthly summaries
    await generateMonthlySummaries();
    
    // 4. Deduplicate player counts
    await deduplicatePlayerCounts();
    
    console.log('Statistics cleanup completed successfully');
  } catch (error) {
    console.error('Error in statistics cleanup:', error);
  }
};

// Archive daily stats older than 90 days
async function archiveOldDailyStats() {
  const ninetyDaysAgo = new Date();
  ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
  const cutoffDate = ninetyDaysAgo.toISOString().split('T')[0];
  
  const oldStatsQuery = query(
    collection(db, 'dailyStats'),
    where('date', '<', cutoffDate),
    limit(50)
  );
  
  const snapshot = await getDocs(oldStatsQuery);
  
  if (snapshot.empty) return;
  
  const batch = writeBatch(db);
  const monthlyData = {};
  
  // Aggregate by month before deleting
  snapshot.docs.forEach(doc => {
    const data = doc.data();
    const month = data.date.substring(0, 7); // YYYY-MM
    
    if (!monthlyData[month]) {
      monthlyData[month] = {
        gamesCreated: 0,
        gamesCompleted: 0,
        gamesAbandoned: 0,
        uniquePlayers: new Set(),
        totalDuration: 0
      };
    }
    
    monthlyData[month].gamesCreated += data.gamesCreated || 0;
    monthlyData[month].gamesCompleted += data.gamesCompleted || 0;
    monthlyData[month].gamesAbandoned += data.gamesAbandoned || 0;
    monthlyData[month].totalDuration += data.totalGameDuration || 0;
    
    if (data.playerIds) {
      data.playerIds.forEach(id => monthlyData[month].uniquePlayers.add(id));
    }
    
    // Delete the daily record
    batch.delete(doc.ref);
  });
  
  // Save monthly summaries
  for (const [month, data] of Object.entries(monthlyData)) {
    await setDoc(doc(db, 'monthlyArchive', month), {
      month: month,
      gamesCreated: data.gamesCreated,
      gamesCompleted: data.gamesCompleted,
      gamesAbandoned: data.gamesAbandoned,
      uniquePlayers: data.uniquePlayers.size,
      avgGameDuration: data.totalDuration / (data.gamesCompleted + data.gamesAbandoned),
      archivedAt: new Date()
    }, { merge: true });
  }
  
  await batch.commit();
  console.log(`Archived ${snapshot.size} old daily stats records`);
}

// Clean up old game events
async function cleanupOldGameEvents() {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  
  const oldEventsQuery = query(
    collection(db, 'gameEvents'),
    where('timestamp', '<', thirtyDaysAgo),
    limit(100)
  );
  
  const snapshot = await getDocs(oldEventsQuery);
  
  if (snapshot.empty) return;
  
  const batch = writeBatch(db);
  let count = 0;
  
  snapshot.docs.forEach(doc => {
    batch.delete(doc.ref);
    count++;
  });
  
  await batch.commit();
  console.log(`Deleted ${count} old game event records`);
}

// Generate monthly summaries
async function generateMonthlySummaries() {
  const currentMonth = new Date().toISOString().slice(0, 7);
  const lastMonth = new Date();
  lastMonth.setMonth(lastMonth.getMonth() - 1);
  const lastMonthStr = lastMonth.toISOString().slice(0, 7);
  
  // Check if last month's summary exists
  const summaryDoc = await getDocs(
    query(collection(db, 'monthlySummaries'), where('month', '==', lastMonthStr))
  );
  
  if (!summaryDoc.empty) return; // Already generated
  
  // Get all daily stats for last month
  const dailyStatsQuery = query(
    collection(db, 'dailyStats'),
    where('date', '>=', `${lastMonthStr}-01`),
    where('date', '<', `${currentMonth}-01`)
  );
  
  const snapshot = await getDocs(dailyStatsQuery);
  
  if (snapshot.empty) return;
  
  const summary = {
    month: lastMonthStr,
    gamesCreated: 0,
    gamesCompleted: 0,
    gamesAbandoned: 0,
    uniquePlayers: new Set(),
    peakConcurrentGames: 0,
    totalDuration: 0,
    customCategoryGames: 0,
    defaultCategoryGames: 0,
    dailyAverages: {
      games: 0,
      players: 0
    },
    popularHours: {},
    popularCategories: {}
  };
  
  // Aggregate data
  snapshot.docs.forEach(doc => {
    const data = doc.data();
    summary.gamesCreated += data.gamesCreated || 0;
    summary.gamesCompleted += data.gamesCompleted || 0;
    summary.gamesAbandoned += data.gamesAbandoned || 0;
    summary.customCategoryGames += data.customCategoryGames || 0;
    summary.defaultCategoryGames += data.defaultCategoryGames || 0;
    summary.totalDuration += data.totalGameDuration || 0;
    summary.peakConcurrentGames = Math.max(summary.peakConcurrentGames, data.peakConcurrentGames || 0);
    
    if (data.playerIds) {
      data.playerIds.forEach(id => summary.uniquePlayers.add(id));
    }
    
    // Aggregate hourly data
    if (data.hourlyBreakdown) {
      Object.entries(data.hourlyBreakdown).forEach(([hour, hourData]) => {
        if (!summary.popularHours[hour]) {
          summary.popularHours[hour] = { games: 0, players: 0 };
        }
        summary.popularHours[hour].games += hourData.games || 0;
        summary.popularHours[hour].players += hourData.players || 0;
      });
    }
    
    // Aggregate category data
    if (data.popularCategories) {
      Object.entries(data.popularCategories).forEach(([category, count]) => {
        summary.popularCategories[category] = (summary.popularCategories[category] || 0) + count;
      });
    }
  });
  
  // Calculate averages
  const daysInMonth = snapshot.size;
  summary.dailyAverages.games = Math.round(summary.gamesCreated / daysInMonth);
  summary.dailyAverages.players = Math.round(summary.uniquePlayers.size / daysInMonth);
  summary.uniquePlayers = summary.uniquePlayers.size;
  
  // Save summary
  await setDoc(doc(db, 'monthlySummaries', lastMonthStr), {
    ...summary,
    generatedAt: new Date()
  });
  
  console.log(`Generated monthly summary for ${lastMonthStr}`);
}

// Deduplicate player counts in daily stats
async function deduplicatePlayerCounts() {
  const today = new Date().toISOString().split('T')[0];
  
  const todayQuery = query(
    collection(db, 'dailyStats'),
    where('date', '==', today),
    limit(1)
  );
  
  const snapshot = await getDocs(todayQuery);
  
  if (snapshot.empty) return;
  
  const doc = snapshot.docs[0];
  const data = doc.data();
  
  if (data.playerIds && Array.isArray(data.playerIds)) {
    // Remove duplicates
    const uniquePlayerIds = [...new Set(data.playerIds)];
    
    if (uniquePlayerIds.length !== data.playerIds.length) {
      await updateDoc(doc.ref, {
        playerIds: uniquePlayerIds,
        uniquePlayers: uniquePlayerIds.length
      });
      console.log(`Deduplicated player count for ${today}`);
    }
  }
}

// Function to manually trigger cleanup (can be called from admin panel)
export const manualCleanup = async () => {
  try {
    await runStatsCleanup();
    return { success: true, message: 'Cleanup completed successfully' };
  } catch (error) {
    return { success: false, error: error.message };
  }
};